import { NavLink } from 'react-router-dom'

import { cn } from '@/lib/utils'
import type { ProjectSectionId, ProjectSectionNav } from '@/features/projects'

type ProjectDashboardNavProps = {
  activeSection: ProjectSectionId
  compact?: boolean
  sections: ProjectSectionNav[]
}

export function ProjectDashboardNav({
  activeSection,
  compact = false,
  sections,
}: ProjectDashboardNavProps) {
  if (compact) {
    return (
      <nav aria-label="Project sections" className="min-w-0">
        <ul className="flex min-w-0 flex-wrap items-center gap-2">
          {sections.map((section) => (
            <li key={section.id}>
              <NavLink
                className={cn(
                  "inline-flex items-center rounded-full border px-4 py-2 text-sm font-semibold transition-colors",
                  section.id === activeSection
                    ? "border-ink/15 bg-ink text-canvas shadow-sm"
                    : "border-white/75 bg-white/72 text-ink hover:bg-white",
                )}
                to={section.href}
              >
                {section.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    )
  }

  return (
    <nav aria-label="Project sections" className="project-shell-panel overflow-hidden p-3">
      <ul className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
        {sections.map((section) => (
          <li key={section.id}>
            <NavLink
              className={cn(
                'flex h-full min-h-[6.25rem] flex-col justify-between gap-4 rounded-[22px] border px-4 py-4 text-left transition-all duration-200',
                section.id === activeSection
                  ? 'border-ink/15 bg-[linear-gradient(150deg,rgba(22,17,13,0.92),rgba(66,45,29,0.82))] text-canvas shadow-panel'
                  : 'border-white/70 bg-[rgba(255,253,249,0.82)] text-muted-foreground hover:-translate-y-0.5 hover:bg-white',
              )}
              to={section.href}
            >
              <span className={cn(
                "text-lg font-semibold",
                section.id === activeSection ? "text-canvas" : "text-foreground",
              )}>
                {section.label}
              </span>
              <span className={cn(
                "text-[11px] uppercase tracking-[0.22em]",
                section.id === activeSection ? "text-canvas/70" : "text-muted-foreground",
              )}>
                {section.availability === 'available'
                  ? 'Workspace'
                  : 'Placeholder'}
              </span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
